import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IFindLeasingCalculator } from '@ncg/data';
import { FeatureDetectionService } from '../../../core/feature-detection.service';
import { ScriptLoadService } from '../../../core/script-load.service';
import { RichTextModule } from '../../../rich-text/rich-text.module';
import { LazyContentData, LazyContentGenericComponent } from '../../models/lazy-content.models';
import { expansionAnimation } from '../../../expansion-panel/expansion.animation';

@Component({
    selector: 'ncg-find-leasing-calculator',
    template: `
        <aside *ngIf="data" [@expandAnimation]="vehicleIsValid ? 'expanded' : 'closed'">
            <!-- Needs the div wrapper for padding, because padding is visible even with height: 0px from expandAnimation -->
            <div [ngClass]="data.layout">
                <div class="mt-5 px-5" *ngIf="data.disclaimer">
                    <ncg-rich-text [html]="data.disclaimer"></ncg-rich-text>
                </div>
                <div
                    #findLeasingRef
                    data-findleasing
                    data-width="100%"
                    [attr.data-seller]="data.sellerId"
                    [attr.data-funding]="data.funding"
                    [attr.data-tax]="data.tax"
                    [attr.data-id]="data.carId"
                ></div>
            </div>
        </aside>
    `,
    standalone: true,
    animations: [expansionAnimation],
    styleUrls: ['./find-leasing-calculator.component.scss'],
    imports: [CommonModule, RichTextModule],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FindLeasingCalculatorComponent implements LazyContentGenericComponent<IFindLeasingCalculator>, OnInit, OnDestroy {
    private readonly scriptUrl = 'https://www.findleasing.nu/offer-sliders.js';
    private readonly _messageHandler = this.messageHandler.bind(this);
    public vehicleIsValid = false;

    @Input()
    public data?: LazyContentData<IFindLeasingCalculator>;

    @ViewChild('findLeasingRef')
    public findLeasingRef?: ElementRef<HTMLDivElement>;

    constructor(
        private readonly scriptLoadService: ScriptLoadService,
        private readonly featureDetectionService: FeatureDetectionService,
        private readonly cd: ChangeDetectorRef
    ) {}

    // handler from FindLeasing adjusted to fit our needs.
    private messageHandler(event: MessageEvent) {
        if (!this.featureDetectionService.isBrowser()) {
            return;
        }
        const identifier = '[FindLeasing]';
        const isFindLeasingData = (s: any) => s.substr(0, identifier.length) === identifier;

        if (event.origin !== 'https://www.findleasing.nu' || !isFindLeasingData(event.data)) {
            return;
        }

        const data = event.data.substr(identifier.length, event.data.length).split(':');
        const command = data[0];

        if (command !== 'init') {
            return;
        }

        // Remove listener again, as we got what we came for
        window.removeEventListener('message', this._messageHandler);
        // Move to the back of event loop with timeout=0, since the animation used is on height so it is pretty costly, so just do it with low prio.
        // Also, FindLeasing does not have the sliders ready when they send the init event... So, delay even more...
        // There are no other events to listen for it seems
        window.setTimeout(() => {
            this.vehicleIsValid = true;
            this.cd.markForCheck();
        }, 750);
    }

    public ngOnInit(): void {
        if (this.featureDetectionService.isBrowser()) {
            window.addEventListener('message', this._messageHandler);
            if (!this.scriptLoadService.isScriptInserted(this.scriptUrl)) {
                this.scriptLoadService.loadScript(this.scriptUrl, { async: true });
            } else {
                setTimeout(() => {
                    const elm = this.findLeasingRef?.nativeElement;
                    if (elm && window.replaceFLIframe) {
                        window.replaceFLIframe(elm);
                    }
                });
            }
        }
    }

    public ngOnDestroy(): void {
        if (this.featureDetectionService.isBrowser()) {
            window.removeEventListener('message', this._messageHandler);
        }
    }
}

declare global {
    interface Window {
        replaceFLIframe?: (element: HTMLDivElement) => void;
    }
}
